'use client';

import { useState } from 'react';
import { AppDownloadModal } from '@components/AppDownloadModal/AppDownloadModal';
import { FAQ } from '@components/FAQ/FAQ';
import { getFAQDataFromContentful } from '@components/FAQ/helpers/getFAQDataFromContentful';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { TypeFaqListWithoutUnresolvableLinksResponse } from '@generated/contentful';
import { CountryDetailFields, FishingWaterRegionFields } from '@generated/graphql';
import { getFAQData } from './helpers/getFAQData';

interface IProps {
  className?: string;
  country: CountryDetailFields | null;
  customFaqList?: TypeFaqListWithoutUnresolvableLinksResponse;
  region?: FishingWaterRegionFields | null;
  topSpecies?: { _id: string; displayName: string; url: string }[];
  topFishingWaters?: { _id: string; displayName: string }[];
  hasRegulations?: boolean;
  title?: string;
  id?: string;
}

export const FAQSection = ({
  className,
  country,
  customFaqList,
  region,
  topFishingWaters,
  topSpecies,
  hasRegulations,
  title,
  id,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const { currentPage } = useAnalytics();

  const customTitle = customFaqList?.fields.title;
  const customFaqs = customFaqList?.fields.faqs;

  const customFaqData = customFaqs ? getFAQDataFromContentful({ faqs: customFaqs }) : [];

  const handleCTAClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'FAQSection',
        source_id: region?.id ?? undefined,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const faqData = getFAQData({
    country,
    handleCTAClick,
    region,
    topSpecies,
    topFishingWaters,
    hasRegulations,
  });

  const hasFaqData = faqData.length > 0 || customFaqData.length > 0;

  if (!hasFaqData) {
    return null;
  }

  return (
    <FAQ
      className={className}
      faqData={[...faqData, ...customFaqData]}
      headingVariant="sm"
      showFAQMetaData
      id={id}
      title={customTitle ?? title}
    >
      <AppDownloadModal
        appDownloadSourceId={region?.id ?? undefined}
        appDownloadSource="FAQSection"
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </FAQ>
  );
};
